import * as React from "react"
import { formatPrice } from "@/lib/format-utils"
import type { BrandStats, BrandOfferSummary } from "./types"

/**
 * "{Brand} by the Numbers" — FRIDGE-401 Step 1, the flagship data band.
 *
 * The card's finding: regional dealers hold bare brand terms with
 * `-overview` pages, and the copyable counter is data a single dealer
 * cannot match. This band leads the page with exactly that data — live
 * cross-retailer price medians and ranges, catalog breadth by family,
 * ENERGY STAR counts, and the capacity span — every figure computed at
 * render from the tracked catalog, nothing typed in.
 *
 * Real-or-Hidden, tile by tile:
 * - The whole band renders NOTHING unless the brand has live appliance
 *   rows AND at least one price figure — a data-poor brand stays calm,
 *   with no empty shells and no zero-tiles.
 * - Each tile requires its own datum: no offers → no offers tile; no
 *   positive capacities → no capacity tile; zero ENERGY STAR rows → no
 *   ENERGY STAR tile.
 *
 * Capacity renders to 2 decimals (the sitewide spec-precision rule).
 */

interface Tile {
  label: string
  value: string
  sub?: string
}

export function BrandNumbersBand({
  brandName,
  stats,
  offerSummary,
}: {
  brandName: string
  stats: BrandStats
  offerSummary: BrandOfferSummary
}) {
  if (stats.productCount <= 0) return null
  if (stats.medianPrice === null && (stats.minPrice === null || stats.maxPrice === null)) return null

  const tiles: Tile[] = []

  tiles.push({
    label: "Models tracked",
    value: stats.productCount.toLocaleString("en-US"),
    sub: [
      stats.fridgeCount > 0 ? `${stats.fridgeCount.toLocaleString("en-US")} refrigerators` : null,
      stats.freezerCount > 0 ? `${stats.freezerCount.toLocaleString("en-US")} freezers` : null,
      stats.specialtyCount > 0 ? `${stats.specialtyCount.toLocaleString("en-US")} specialty cooling` : null,
    ]
      .filter(Boolean)
      .join(" · ") || undefined,
  })

  if (stats.medianPrice !== null) {
    tiles.push({
      label: "Median tracked price",
      value: formatPrice(stats.medianPrice),
      sub: "across the live catalog",
    })
  }

  if (stats.minPrice !== null && stats.maxPrice !== null) {
    tiles.push({
      label: "Price range",
      value: `${formatPrice(stats.minPrice)}–${formatPrice(stats.maxPrice)}`,
      sub: "lowest tracked offer per model",
    })
  }

  if (offerSummary.offerCount > 0) {
    tiles.push({
      label: "Live offers",
      value: offerSummary.offerCount.toLocaleString("en-US"),
      sub:
        offerSummary.retailerCount >= 2
          ? `in stock across ${offerSummary.retailerCount} retailers`
          : "in stock right now",
    })
  }

  if (stats.energyStarCount > 0) {
    tiles.push({
      label: "ENERGY STAR certified",
      value: stats.energyStarCount.toLocaleString("en-US"),
      sub: `of ${stats.productCount.toLocaleString("en-US")} tracked models`,
    })
  }

  if (stats.capacityMinCuft !== null && stats.capacityMaxCuft !== null) {
    tiles.push({
      label: "Capacity span",
      value: `${stats.capacityMinCuft.toFixed(2)}–${stats.capacityMaxCuft.toFixed(2)}`,
      sub: "cubic feet",
    })
  }

  return (
    <section
      id="brand-numbers"
      aria-label={`${brandName} by the numbers`}
      className="scroll-mt-[110px]"
    >
      <div className="rounded-2xl border border-gray-200 bg-gray-50/70 px-5 py-5 sm:px-7 sm:py-6">
        <h2 className="mb-4 text-[17px] font-black tracking-[-0.01em] text-gray-900 sm:text-[19px]">
          {brandName} by the Numbers
        </h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-6">
          {tiles.map((t) => (
            <div key={t.label} className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                {t.label}
              </dt>
              <dd className="mt-1">
                {/* break-words, never truncate: a measured figure with an
                    ellipsis in it ("$37.99–$3,…") is a wrong number. The wide
                    price range wraps to a second line instead — same rule as
                    the collection band. */}
                <span className="block break-words text-[20px] font-black leading-tight tracking-[-0.02em] text-gray-900 sm:text-[22px]">
                  {t.value}
                </span>
                {t.sub && (
                  <span className="mt-0.5 block text-[12px] leading-snug text-gray-500">
                    {t.sub}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  )
}
